import { useState, useEffect, useCallback } from "react";
import { useConnectivity } from "@/contexts/ConnectivityContext";
import { getPendingCount, flush } from "@/lib/syncQueue";
import { getMeta } from "@/lib/offlineDb";

export function useSyncStatus() {
  const { isOnline } = useConnectivity();
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);

  const refreshCounts = useCallback(async () => {
    const count = await getPendingCount();
    setPendingCount(count);
    const lastSync = await getMeta("lastFullSync");
    setLastSyncTime(lastSync);
  }, []);

  // Poll pending count
  useEffect(() => {
    refreshCounts();
    const id = setInterval(refreshCounts, 5000);
    return () => clearInterval(id);
  }, [refreshCounts]);

  // Auto-flush when coming back online
  useEffect(() => {
    if (!isOnline) return;
    const doFlush = async () => {
      const count = await getPendingCount();
      if (count === 0) return;
      setIsSyncing(true);
      try {
        await flush();
      } finally {
        setIsSyncing(false);
        refreshCounts();
      }
    };
    doFlush();
    // Also flush every 30s while online
    const id = setInterval(doFlush, 30_000);
    return () => clearInterval(id);
  }, [isOnline, refreshCounts]);

  const forceSync = useCallback(async () => {
    setIsSyncing(true);
    try {
      await flush();
    } finally {
      setIsSyncing(false);
      refreshCounts();
    }
  }, [refreshCounts]);

  return { pendingCount, lastSyncTime, isSyncing, forceSync, refreshCounts };
}
